import React, { useCallback, useMemo, useRef, useState } from "react";
import { SubMenuListProps } from "./subMenues/SubMenuList.tsx";

export function useSubMenuList(props: SubMenuListProps) {
    const { data, onChange, onLeftArrowClick, chosenValues, separateListForSelectedValue } = props;
    const subMenuItemRefs = useRef<Map<string, HTMLElement>>(new Map());

    const [submenuInputValue, setSubmenuInputValue] = useState("");
    const [submenuHighlightedIndex, setSubmenuHighlightedIndex] = useState(-1);

    // Hovering re-renders this hook's owner through submenuHighlightedIndex, and a list of several hundred rows
    // cannot afford to re-filter and re-lowercase on every mouse move, so the derived lists are memoized on the
    // inputs that actually change them.
    const chosenIds = useMemo(() => new Set(chosenValues.map((item) => item.id)), [chosenValues]);

    const filteredSubmenuItems = useMemo(() => {
        const needle = submenuInputValue.toLowerCase();
        const matching = needle ? data.filter((item) => item.name.toLowerCase().includes(needle)) : data;
        return separateListForSelectedValue ? matching.filter((item) => !chosenIds.has(item.id)) : matching;
    }, [data, submenuInputValue, separateListForSelectedValue, chosenIds]);

    // The rows are memoized so that a hover re-renders two of them rather than the whole list. A callback recreated
    // on every render would defeat that, so the row callbacks keep their identity and read the latest props here.
    const latestProps = useRef(props);
    latestProps.current = props;

    const handleItemSelect = useCallback((id: string) => {
        const { data: latestData, chosenValues: latestChosenValues, onChange: latestOnChange } = latestProps.current;
        const item = latestData.find((x) => x.id === id) ?? latestChosenValues.find((x) => x.id === id);
        if (item) {
            latestOnChange(item);
        }
    }, []);

    const handleSubmenuItemHover = useCallback((index: number) => {
        setSubmenuHighlightedIndex(index);
    }, []);

    const registerItemRef = useCallback((id: string, element: HTMLElement | null) => {
        if (element) {
            subMenuItemRefs.current.set(id, element);
        } else {
            subMenuItemRefs.current.delete(id);
        }
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, source: "main" | "submenu") => {
        if (!open) return;

        switch (e.key) {
            case "ArrowDown": {
                e.preventDefault();
                let newIndex = submenuHighlightedIndex < filteredSubmenuItems.length - 1 ? submenuHighlightedIndex + 1 : submenuHighlightedIndex;
                const currentItem = filteredSubmenuItems[newIndex];

                const itemRef = subMenuItemRefs.current.get(currentItem.id);
                itemRef?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });

                setSubmenuHighlightedIndex(newIndex);
                break;
            }

            case "ArrowUp": {
                e.preventDefault();
                let newIndex = submenuHighlightedIndex > 0 ? submenuHighlightedIndex - 1 : submenuHighlightedIndex;
                const currentItem = filteredSubmenuItems[newIndex];

                const itemRef = subMenuItemRefs.current.get(currentItem.id);
                itemRef?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });

                setSubmenuHighlightedIndex(newIndex);
                break;
            }

            case "ArrowLeft":
                if (onLeftArrowClick) {
                    e.preventDefault();
                    onLeftArrowClick();
                }
                break;

            case "Enter":
                e.preventDefault();

                if (submenuHighlightedIndex >= 0 && submenuHighlightedIndex < filteredSubmenuItems.length) {
                    const submenuItem = filteredSubmenuItems[submenuHighlightedIndex];
                    onChange(submenuItem);
                }

                break;
        }
    };

    return {
        setSubmenuInputValue,
        submenuInputValue,
        handleKeyDown,
        submenuHighlightedIndex,
        filteredSubmenuItems,
        chosenIds,
        handleItemSelect,
        handleSubmenuItemHover,
        registerItemRef,
        setSubmenuHighlightedIndex,
    };
}
